import React, { useState, useEffect } from "react";
import { Search, MapPin, Eye, ArrowLeft } from "lucide-react";
import { useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const AddExperiencePage = () => {
  const navigate = useNavigate();
  const { userData } = useOutletContext();
  const userCity = userData?.city;
  
  const [availableActivities, setAvailableActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchAvailableActivities();
  }, []);

  const fetchAvailableActivities = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token_partner_activities");
      if (!token) { navigate("/login"); return; }

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/task/available-activities`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableActivities(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load activity marketplace');
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = availableActivities.filter(activity => {
    const matchesSearch =
      activity.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.description?.toLowerCase().includes(searchTerm.toLowerCase());

    if (userCity) {
      const normalizedUserCity = userCity.toLowerCase().trim();
      const cityMatches = Array.isArray(activity.city) &&
        activity.city.some(city => {
          const normalizedCity = city.toLowerCase().trim();
          return normalizedCity === normalizedUserCity || normalizedCity.includes(normalizedUserCity) || normalizedUserCity.includes(normalizedCity);
        });
      return matchesSearch && cityMatches;
    }

    return matchesSearch;
  });


  return (
    <div className="space-y-8 animate-in fade-in duration-700 pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <button 
            onClick={() => navigate('/activity/listing')}
            className="flex items-center gap-2 text-gray-500 hover:text-primary transition-colors font-bold text-sm mb-4"
          >
            <ArrowLeft size={16} /> Back to Inventory
          </button>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight uppercase">
            Experience Marketplace
          </h1>
          <p className="text-[10px] font-black text-gray-400 mt-1 uppercase tracking-widest">
            Select pre-verified experiences to showcase to your guests
          </p>
        </div>
      </div>

      <div className="px-6 py-4 bg-white border border-gray-100 rounded-[1.5rem] shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
          <div className="flex-1 relative group">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-primary transition-colors" size={20} />
            <input
              type="text"
              placeholder="Search activity marketplace..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-6 py-3 text-sm bg-gray-50 border border-transparent rounded-xl focus:outline-none focus:bg-white focus:border-primary/30 focus:ring-4 focus:ring-primary/10 transition-all font-bold text-gray-900"
            />
          </div>

          {userCity && (
            <div className="flex items-center gap-2 px-6 py-3 bg-primary/5 border border-primary/10 rounded-xl">
              <MapPin size={18} className="text-primary" />
              <span className="text-xs font-black text-primary uppercase tracking-widest">{userCity}</span>
            </div>
          )}
        </div>
      </div>

      <div className="">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full space-y-6">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary" />
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] animate-pulse">Syncing with Marketplace...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {filteredActivities.map((activity) => (
              <div 
                key={activity._id} 
                onClick={() => navigate(`/activity/preview-experience/${activity._id}`, { state: { activity } })}
                className="group bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 flex flex-col cursor-pointer"
              >
                <div className="relative h-44 sm:h-48 overflow-hidden bg-slate-50">
                  {activity.imageUrls?.[0] ? (
                    <img src={activity.imageUrls[0]} alt={activity.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full bg-slate-50 flex items-center justify-center text-slate-300"><MapPin size={40} /></div>
                  )}
                  <div className="absolute top-3 left-3">
                    <div className="px-3 py-1 bg-black/60 backdrop-blur-md text-white text-[9px] font-black rounded-lg uppercase tracking-widest flex items-center gap-1.5 border border-white/10"><MapPin size={10} /> {activity.city?.[0] || 'Global'}</div>
                  </div>
                </div>
                <div className="p-4 sm:p-5 flex flex-col flex-1">
                  <h3 className="text-sm sm:text-base font-black text-slate-900 mb-1 uppercase tracking-tight line-clamp-1 group-hover:text-primary transition-colors">{activity.name}</h3>
                  <p className="text-xs text-slate-500 font-medium line-clamp-2 mb-4 flex-1 leading-relaxed">{activity.description}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Market Rate</p>
                      <p className="text-xl sm:text-2xl font-black text-primary tracking-tight">₹{activity.price}</p>
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/activity/preview-experience/${activity._id}`, { state: { activity } });
                      }} 
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-500 group-hover:bg-primary/10 group-hover:text-primary rounded-xl transition-all font-bold text-xs"
                      title="View Experience"
                    >
                      <Eye size={14} />
                      <span>View</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default AddExperiencePage;
